import { on, fire } from "delegated-events";
import { CountUp } from "countup.js";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const runCounter = (el) => {
  const useGrouping = el.dataset.useGrouping;
  const decimalPlaces = el.dataset.decimalPlaces;
  const end = el.dataset.end;

  const opts = {
    useGrouping: !!+useGrouping,
    duration: 1,
  };

  if (decimalPlaces) {
    opts.decimalPlaces = decimalPlaces;
  }

  const counter = new CountUp(el, end, opts);

  counter.start();
};

const chunk = (inputArray) => {
  const perChunk = 2;

  return inputArray.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / perChunk);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);
};

const runMobileTimeline = (el) => {
  const trigger = el.querySelector(".element-container");
  const blocksGrid = el.querySelectorAll(".stats-grid");
  const blocks = el.querySelectorAll(".stats-block");
  const numBlocks = blocks.length;

  gsap.set(el.querySelector(".element-content").children, { opacity: 0, y: "25%" });

  gsap.to(el.querySelector(".element-content").children, {
    opacity: 1,
    y: 0,
    duration: 0.8,
    stagger: 0.3,
    scrollTrigger: {
      trigger: trigger,
      start: "top center",
      once: true,
      markers: false,
    },
  });

  gsap.set(blocksGrid, { xPercent: 100 });

  const timeline = gsap.timeline({
    paused: true,
    scrollTrigger: {
      trigger: trigger,
      pin: true,
      markers: false,
      scrub: 1,
      start: () => {
        const siteHeaderHeight = document.querySelector(".site-header-inner").offsetHeight;

        return `top ${siteHeaderHeight}`;
      },
      end: () => `+=${trigger.offsetHeight * 3}`,
    },
  });

  timeline.to(blocksGrid, {
    xPercent: -100 * (numBlocks - 1),
    ease: "none",
  });

  blocks.forEach((block) => {
    const value = block.querySelector(".stats-value");

    gsap.set(block, { opacity: 0, y: "10%" });

    gsap.to(block, {
      opacity: 1,
      y: 0,
      scrollTrigger: {
        trigger: block,
        containerAnimation: timeline,
        start: "left 60%",
        markers: false,
      },
      onStart: () => {
        runCounter(value.querySelector("[data-counter]"));
      },
    });
  });

  timeline.to(el, {
    position: "relative",
    duration: 0,
    onComplete: () => {
      if (!el.classList.contains("is-complete")) {
        fire(document.documentElement, "element-results:complete", el);
      }
    },
  });
};

const runDesktopTimeline = (el) => {
  const trigger = el.querySelector(".element-container");
  const isHomePage = document.body.classList.contains("pagetype-homepage");

  let isHomePageAutoScroll = false;

  if (isHomePage) {
    const scrollContainer = document.querySelector(".panel-scroll-container");
    const hasAutoScroll = scrollContainer.dataset.autoscroll !== undefined;
    const canAutoScroll = window.matchMedia("(min-height: 900px) and (min-width: 1024px)").matches;

    isHomePageAutoScroll = hasAutoScroll && canAutoScroll;
  }

  gsap.set(el.querySelector(".element-content").children, { opacity: 0, y: "25%" });

  gsap.to(el.querySelector(".element-content").children, {
    opacity: 1,
    y: 0,
    duration: 0.8,
    stagger: 0.3,
    scrollTrigger: {
      trigger: trigger,
      start: "top center",
      once: true,
      markers: false,
    },
  });

  const scrollTrigger = {
    trigger: trigger,
    pin: true,
    markers: false,
    scrub: 2,
    start: () => {
      const siteHeaderHeight = document.querySelector(".site-header-inner").offsetHeight;

      return `top ${siteHeaderHeight}`;
    },
    end: () => `+=${trigger.offsetHeight * 2}`,
    onLeave: function (self) {
      let start = self.start;
      self.scroll(self.start);
      self.disable(false, true);
      ScrollTrigger.refresh();
      window.scrollTo(0, start);
    },
  };

  if (!isHomePageAutoScroll) {
    scrollTrigger.onEnterBack = (self) => {
      self.scroll(self.start);
    };

    scrollTrigger.onLeaveBack = () => {
      gsap.to(el.querySelector(".stats-grid"), {
        opacity: 0,
        onComplete: () => {
          el.classList.remove("is-complete");

          setTimeout(() => {
            el.querySelector(".stats-grid").style = null;
          }, 500);
        },
      });
    };
  }

  const timeline = gsap.timeline({
    paused: true,
    scrollTrigger,
  });

  const blocks = el.querySelectorAll(".stats-block");
  const chunkedBlocks = chunk(Array.from(blocks));

  blocks.forEach((block) => {
    gsap.set(block, { opacity: 0, y: "20%" });
  });

  chunkedBlocks.forEach((chunks) => {
    timeline.to(chunks, {
      opacity: 1,
      y: 0,
      ease: "power4",
      delay: 1,
      duration: 2,
      stagger: 0.4,
      onStart: function () {
        chunks.forEach((block) => {
          runCounter(block.querySelector(".stats-value [data-counter]"));
        });
      },
    });
  });

  timeline.to(el, {
    position: "relative",
    duration: 0,
    onComplete: () => {
      if (!el.classList.contains("is-complete")) {
        if (window.matchMedia("(min-width: 768px)").matches) {
          fire(document.documentElement, "element-results:complete", el);
        }
      }
    },
  });

  timeline.to(blocks[blocks.length - 1], {
    position: "relative",
    duration: 5,
  });
};

document.querySelectorAll(".element-results").forEach((el) => {
  const panelContainer = el.querySelector(".animation-panel-container");

  if (window.matchMedia("(min-width: 768px)").matches) {
    runDesktopTimeline(el);
  } else {
    runMobileTimeline(el);
  }

  panelContainer.setAttribute("data-animation-ready", true);
});

on("element-results:complete", "html", (e) => {
  e.detail.classList.add("is-complete");
});
